import { ContractAddress, DepositInfo, Reward, RewardInfo, TDeposit, TDepositForServer, TRewardForServer } from '../../../shared/api/contract';
import { ContractAbi } from 'web3';

import { ConnectorType } from '@reown/appkit-core';
import { depositFormat } from '../lib/deposit-info';
import { MoonStaking } from '../../../shared/api/contract/ethers/moon-staking';
import { GetMainRewardService } from './get-mainreward-service.ts';
import { anyTokenSymbolAbi, contractABI } from '../../../shared/api/contract/config.ts';
import { ethers } from 'ethers';
import { formatDepositsForServerToDeposits } from '../../../shared/utils/deposit.ts';

export type GetRewardsInfo = {
    timestamp: bigint;
    rewardToken: string;
    rewardAmount: bigint;
};

export class GetDepositsService extends MoonStaking {
    constructor(contractAddress: ContractAddress, abi: ContractAbi, provider: ConnectorType, walletAddress: ContractAddress) {
        super(contractAddress, abi, provider, walletAddress);
    }

    async getDeposits(address: ContractAddress, saved = true): Promise<[TDeposit[], bigint[]]> {
        try {
            const depositIndexes = await this.getUserDepositIndexes(address);

            if (!depositIndexes) [];

            const deposits = await this.getDepositsContract(address, depositIndexes);

            if (!saved) return [[...deposits], depositIndexes];

            const savedDeposits = await this.getSavedDeposits(address);

            return [[...deposits, ...savedDeposits], depositIndexes];
        } catch (error) {
            console.error('Error fetching deposits:', error);
            return [[], []];
        }
    }

    private async getDepositsContract(userAddress: ContractAddress, depositIndexes: bigint[]): Promise<TDeposit[]> {
        try {
            const deposits = (await this.contract.getUserDeposits(userAddress, depositIndexes)) as DepositInfo[];

            console.log(depositIndexes);

            const result: TDeposit[] = await Promise.all(
                deposits.map(
                    async (deposit, i): Promise<TDeposit> => ({
                        ...depositFormat(deposit),
                        reward: Number(await this.getMainReward(userAddress, depositIndexes[i])),
                        rewards: await this.getAllAdditionalRewardTokens(userAddress, depositIndexes[i], Number(deposit.lastRewardDeposit)),
                        index: depositIndexes[i],
                    })
                )
            );

            return result;
        } catch (error) {
            console.error('Error fetching deposits:', error);
            return [];
        }
    }

    async getUserDepositIndexes(userAddress: string): Promise<bigint[]> {
        try {
            const indexes = (await this.contract.getUserActualDepositIndexes(userAddress)) as bigint[];

            return indexes;
        } catch (error) {
            console.error('Error fetching deposit indexes:', error);
            return [];
        }
    }

    private async getSavedDeposits(address: ContractAddress): Promise<TDeposit[]> {
        try {
            const res = await fetch(`${import.meta.env.VITE_DEPOSIT_API}/${address}`);

            if (!res.ok) return [];
            const deposits: TDepositForServer[] = await res.json();

            return formatDepositsForServerToDeposits(deposits);
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    async getMainReward(address: ContractAddress, index: bigint): Promise<string> {
        try {
            const reward = (await this.contract.pendingRewardMain(address, index)) as bigint;

            return ethers.utils.formatEther(reward);
        } catch (error) {
            console.log(error);
            return '0';
        }
    }

    async getAllAdditionalRewardTokens(address: ContractAddress, depositIndex: bigint, lastRewardDeposit: number): Promise<Reward[]> {
        try {
            const lastRewardIndex = Number(await this.getAdditionalRewardLastIndex());

            if (lastRewardDeposit === lastRewardIndex) return [];

            const rewardIndexes: bigint[] = [];

            for (let i = lastRewardDeposit; i < lastRewardIndex; i++) {
                rewardIndexes.push(BigInt(i));
            }

            if (!rewardIndexes.length) return [];
            return await this.getRewards(address, depositIndex, rewardIndexes);
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    private async getAdditionalRewardLastIndex(): Promise<number | undefined> {
        try {
            const index = (await this.contract.getAdditionalRewardLastIndex()) as number;

            return index;
        } catch (error) {
            console.log(error);
            return undefined;
        }
    }

    private async getRewardInfo(rewardIndex: bigint): Promise<RewardInfo | undefined> {
        try {
            const reawardInfo = (await this.contract.rewardInfo(rewardIndex)) as RewardInfo;

            return reawardInfo;
        } catch (error) {
            console.log(error);
            return undefined;
        }
    }

    private async pendingRewards(address: ContractAddress, depositIndex: bigint, rewardIndexes: bigint[]): Promise<bigint[]> {
        try {
            const rewardInfos = (await this.contract.pendingRewards(address, depositIndex, rewardIndexes)) as bigint[];
            return rewardInfos;
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    private async getMoreInfoForReward(contractAddress: string): Promise<string> {
        try {
            const contract = new ethers.Contract(contractAddress, anyTokenSymbolAbi, this.signer);

            const symbol = (await contract.symbol()) as string;

            return symbol;
        } catch (error) {
            console.log(error);
            return '';
        }
    }

    private mergedRewards(rewards: Reward[]): Reward[] {
        const mergedData = Object.values(
            rewards.reduce((acc, { symbol, amount }) => {
                const am = BigInt(amount.toString());
                if (!acc[symbol]) {
                    acc[symbol] = { symbol, amount: 0n };
                }
                acc[symbol].amount += am;
                return acc;
            }, {} as Record<string, { symbol: string; amount: bigint }>)
        );

        return mergedData;
    }

    private async getRewards(address: ContractAddress, depositIndex: bigint, rewadIndexes: bigint[]): Promise<Reward[]> {
        try {
            const rewards = await this.pendingRewards(address, depositIndex, rewadIndexes);
            const rewardsInfoAll: RewardInfo[] = await this.contract.getRewardInfo();
            const rewardsAddreses: string[] = rewadIndexes.map((index) => rewardsInfoAll[Number(index)].token);
            const uniqueRewardsAddreses: string[] = [...new Set(rewardsAddreses)];

            const moreInfosForToken: { symbol: string; token: string }[] = await Promise.all(
                uniqueRewardsAddreses.map(async (token): Promise<{ symbol: string; token: string }> => {
                    return {
                        token,
                        symbol: await this.getMoreInfoForReward(token),
                    };
                })
            );
            console.log(moreInfosForToken);
            return [
                ...rewards.map((reward, index) => ({
                    amount: reward,
                    imgUri: '',
                    symbol: moreInfosForToken.find((item) => item.token.toLowerCase() === rewardsAddreses[index].toLowerCase())?.symbol || '',
                })),
            ];
        } catch (error) {
            console.log(error);
            return [];
        }
    }

    private formatStringToInt(rewards: TRewardForServer[]): Reward[] {
        return [
            ...rewards.map(
                (reward): Reward => ({
                    amount: BigInt(reward.amount),
                    imgUri: '',
                    symbol: reward.symbol,
                })
            ),
        ];
    }
}
